import React from "react";
import { Composition, Folder } from "remotion";
import { Studio } from "./Studio";
import { Showcase } from "./Showcase";

export interface TerminalTab {
  label: string;
  method: string;
  route: string;
  content: string;
}

export interface TemplateProps {
  videoUrl: string;
  videoDuration: number;
  platform: "ios" | "android" | "web";
  title: string;
  titleLines?: string[];
  subtitle?: string;
  terminalTabs: TerminalTab[];
  hasNetworkData: boolean;
  /** Showcase display mode: 'artistic' = staggered fonts, no terminal; 'terminal' = plain title + terminal */
  showcaseMode?: "artistic" | "terminal";
}

const FPS = 30;

const sampleTabs: TerminalTab[] = [
  {
    label: "GET /api/users",
    method: "GET",
    route: "/api/users",
    content: JSON.stringify(
      [
        { id: 1, name: "Alice Chen", email: "alice@example.com", role: "admin" },
        { id: 2, name: "Bob Park", email: "bob@example.com", role: "user" },
      ],
      null,
      2
    ),
  },
  {
    label: "POST /api/auth/login",
    method: "POST",
    route: "/api/auth/login",
    content: JSON.stringify(
      { token: "eyJhbGciOiJIUzI1NiIs...", expiresIn: 3600, user: { id: 1, name: "Alice" } },
      null,
      2
    ),
  },
  {
    label: "GET /api/products",
    method: "GET",
    route: "/api/products",
    content: JSON.stringify(
      [
        { id: "p1", name: "Pro Plan", price: 29.99, currency: "USD" },
        { id: "p2", name: "Team Plan", price: 79.99, currency: "USD" },
      ],
      null,
      2
    ),
  },
  {
    label: "PUT /api/settings",
    method: "PUT",
    route: "/api/settings",
    content: JSON.stringify(
      { theme: "dark", notifications: true, language: "en", timezone: "UTC-3" },
      null,
      2
    ),
  },
  {
    label: "GET /api/analytics",
    method: "GET",
    route: "/api/analytics",
    content: JSON.stringify(
      { dailyUsers: 1243, retention: "87%", avgSession: "4m 32s" },
      null,
      2
    ),
  },
];

const networkProps: TemplateProps = {
  videoUrl:
    "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4",
  videoDuration: 10,
  platform: "ios",
  title: "Network requests captured during recording session",
  subtitle: "AppLab Discovery",
  terminalTabs: sampleTabs,
  hasNetworkData: true,
};

const noNetworkProps: TemplateProps = {
  ...networkProps,
  title: "Present your ideas with beautiful videos",
  titleLines: ["Present your", "ideas with", "beautiful", "videos"],
  terminalTabs: [],
  hasNetworkData: false,
};

export const Root: React.FC = () => {
  return (
    <>
      <Folder name="Templates">
        <Composition
          id="studio"
          component={Studio}
          durationInFrames={300}
          fps={FPS}
          width={1920}
          height={1080}
          defaultProps={networkProps}
        />
        <Composition
          id="showcase"
          component={Showcase}
          durationInFrames={360}
          fps={FPS}
          width={1920}
          height={1080}
          defaultProps={{ ...noNetworkProps, showcaseMode: "artistic" }}
        />
      </Folder>

      {/* Variants for preview */}
      <Folder name="Variants">
        <Composition
          id="studio-no-network"
          component={Studio}
          durationInFrames={300}
          fps={FPS}
          width={1920}
          height={1080}
          defaultProps={noNetworkProps}
        />
        <Composition
          id="studio-android"
          component={Studio}
          durationInFrames={300}
          fps={FPS}
          width={1920}
          height={1080}
          defaultProps={{ ...networkProps, platform: "android" }}
        />
        <Composition
          id="studio-web"
          component={Studio}
          durationInFrames={300}
          fps={FPS}
          width={1920}
          height={1080}
          defaultProps={{ ...networkProps, platform: "web" }}
        />
        <Composition
          id="showcase-terminal"
          component={Showcase}
          durationInFrames={360}
          fps={FPS}
          width={1920}
          height={1080}
          defaultProps={{ ...networkProps, showcaseMode: "terminal" }}
        />
        <Composition
          id="showcase-network"
          component={Showcase}
          durationInFrames={360}
          fps={FPS}
          width={1920}
          height={1080}
          defaultProps={networkProps}
        />
      </Folder>
    </>
  );
};
